/**
 * Carrom Game Engine
 *
 * Game rules and state management for Carrom.
 * Handles: initialization, coin layout, shot processing, foul detection,
 * queen cover rules, turn management, and win detection.
 */

import {
  CarromGameState,
  CarromGameStatus,
  CarromPlayer,
  CoinState,
  CoinType,
  CoinLayout,
  ShotInput,
  ShotResult,
  MoveRecord,
  PlayerType,
  BOARD_SIZE,
  BORDER_WIDTH,
  CENTER,
  COIN_RADIUS,
  STRIKER_RADIUS,
  BASELINE_OFFSET,
} from './types';
import { CarromPhysics } from './CarromPhysics';
import { PhysicsBody, DEFAULT_PHYSICS_CONFIG } from './types';

export class CarromEngine {
  private state: CarromGameState;
  private physics: CarromPhysics;

  constructor() {
    this.physics = new CarromPhysics();
    this.state = this.createInitialState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  private createInitialState(): CarromGameState {
    return {
      gameId: `carrom_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      coins: [],
      currentTurn: 'player1',
      player1Score: 0,
      player2Score: 0,
      player1PocketedCoins: [],
      player2PocketedCoins: [],
      player1Assignment: 'white',
      player2Assignment: 'black',
      queenPocketed: false,
      queenCovered: false,
      queenPocketedBy: undefined,
      pendingQueenCover: false,
      pendingQueenCoverPlayer: undefined,
      gameStatus: { type: 'active' },
      playerTypes: { player1: 'human', player2: 'human' },
      moveHistory: [],
      startedAt: Date.now(),
      lastMoveAt: Date.now(),
      consecutiveFouls: { player1: 0, player2: 0 },
    };
  }

  initializeGame(
    playerTypes: Record<CarromPlayer, PlayerType>,
    player1Assignment: 'white' | 'black' = 'white',
    coinLayout: CoinLayout = 'standard'
  ): void {
    this.state = this.createInitialState();
    this.state.playerTypes = playerTypes;
    this.state.player1Assignment = player1Assignment;
    this.state.player2Assignment = player1Assignment === 'white' ? 'black' : 'white';

    // Create coins in selected layout
    this.state.coins = this.createCoinsForLayout(coinLayout);

    // Set up physics bodies
    this.setupPhysicsBodies();
  }

  /**
   * Re-layout coins without resetting the full game state.
   * Used when changing layout mid-game (only affects unpocketed coins).
   */
  resetCoinLayout(layout: CoinLayout): void {
    const newCoins = this.createCoinsForLayout(layout);
    // Preserve pocketed status from current state
    for (const coin of this.state.coins) {
      if (coin.pocketed) {
        const newCoin = newCoins.find(c => c.id === coin.id);
        if (newCoin) {
          newCoin.pocketed = true;
          newCoin.pocketedBy = coin.pocketedBy;
        }
      }
    }
    this.state.coins = newCoins;
    this.setupPhysicsBodies();
  }

  private createCoinsForLayout(layout: CoinLayout): CoinState[] {
    switch (layout) {
      case 'horizontal': return this.createHorizontalLayout();
      case 'vertical': return this.createVerticalLayout();
      default: return this.createStandardLayout();
    }
  }

  private createStandardLayout(): CoinState[] {
    const coins: CoinState[] = [];

    // Center: Queen (red)
    coins.push({
      id: 'queen',
      type: 'queen',
      x: CENTER,
      y: CENTER,
      pocketed: false,
    });

    // Inner ring: 6 coins at radius 26, alternating black/white, 60° apart
    const innerRadius = 26;
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const type: CoinType = i % 2 === 0 ? 'black' : 'white';
      coins.push({
        id: `${type}_inner_${i}`,
        type,
        x: CENTER + Math.cos(angle) * innerRadius,
        y: CENTER + Math.sin(angle) * innerRadius,
        pocketed: false,
      });
    }

    // Outer ring: 12 coins at radius 52, alternating white/black, 30° apart
    const outerRadius = 52;
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12;
      const type: CoinType = i % 2 === 0 ? 'white' : 'black';
      coins.push({
        id: `${type}_outer_${i}`,
        type,
        x: CENTER + Math.cos(angle) * outerRadius,
        y: CENTER + Math.sin(angle) * outerRadius,
        pocketed: false,
      });
    }

    return coins;
  }

  private createHorizontalLayout(): CoinState[] {
    const coins: CoinState[] = [];

    // Queen in center
    coins.push({ id: 'queen', type: 'queen', x: CENTER, y: CENTER, pocketed: false });

    // 9 white coins on the left, 9 black on the right, spread horizontally
    const spacing = 28;
    const startX = CENTER - spacing * 4.5;

    for (let i = 0; i < 9; i++) {
      coins.push({
        id: `white_h_${i}`,
        type: 'white',
        x: startX + i * spacing,
        y: CENTER - COIN_RADIUS * 2.5,
        pocketed: false,
      });
    }
    for (let i = 0; i < 9; i++) {
      coins.push({
        id: `black_h_${i}`,
        type: 'black',
        x: startX + i * spacing,
        y: CENTER + COIN_RADIUS * 2.5,
        pocketed: false,
      });
    }

    return coins;
  }

  private createVerticalLayout(): CoinState[] {
    const coins: CoinState[] = [];

    // Queen in center
    coins.push({ id: 'queen', type: 'queen', x: CENTER, y: CENTER, pocketed: false });

    // 9 white coins on top, 9 black on bottom, spread vertically
    const spacing = 28;
    const startY = CENTER - spacing * 4.5;

    for (let i = 0; i < 9; i++) {
      coins.push({
        id: `white_v_${i}`,
        type: 'white',
        x: CENTER - COIN_RADIUS * 2.5,
        y: startY + i * spacing,
        pocketed: false,
      });
    }
    for (let i = 0; i < 9; i++) {
      coins.push({
        id: `black_v_${i}`,
        type: 'black',
        x: CENTER + COIN_RADIUS * 2.5,
        y: startY + i * spacing,
        pocketed: false,
      });
    }

    return coins;
  }

  private setupPhysicsBodies(): void {
    this.physics.clearBodies();

    for (const coin of this.state.coins) {
      this.physics.addBody({
        id: coin.id,
        x: coin.x,
        y: coin.y,
        vx: 0,
        vy: 0,
        radius: coin.type === 'queen' ? COIN_RADIUS : COIN_RADIUS,
        mass: 1,
        isStatic: coin.pocketed,
        type: coin.type,
        pocketed: coin.pocketed,
      });
    }
  }

  // =========================================================================
  // Shot Processing
  // =========================================================================

  /**
   * Get the baseline Y position for the current player.
   * Player 1 shoots from the bottom, Player 2 from the top.
   */
  getBaselineY(player: CarromPlayer): number {
    return player === 'player1'
      ? BOARD_SIZE - BORDER_WIDTH - BASELINE_OFFSET
      : BORDER_WIDTH + BASELINE_OFFSET;
  }

  /**
   * Get the valid X range for striker placement on the baseline.
   */
  getStrikerXRange(): { min: number; max: number } {
    const margin = BORDER_WIDTH + STRIKER_RADIUS + 40;
    return {
      min: margin,
      max: BOARD_SIZE - margin,
    };
  }

  /**
   * Create a striker physics body at the given position.
   */
  createStriker(strikerX: number, player: CarromPlayer): PhysicsBody {
    const baselineY = this.getBaselineY(player);
    const xRange = this.getStrikerXRange();
    const clampedX = Math.max(xRange.min, Math.min(xRange.max, strikerX));

    // Remove existing striker if any
    this.physics.removeBody('striker');

    const striker: PhysicsBody = {
      id: 'striker',
      x: clampedX,
      y: baselineY,
      vx: 0,
      vy: 0,
      radius: STRIKER_RADIUS,
      mass: 1.5,
      isStatic: false,
      type: 'striker',
      pocketed: false,
    };

    this.physics.addBody(striker);
    return striker;
  }

  /**
   * Execute a shot and return the physics engine for animation.
   * The caller should run the physics loop and call processResults when done.
   */
  executeShot(shot: ShotInput): void {
    const player = this.state.currentTurn;
    this.createStriker(shot.strikerX, player);
    this.physics.applyShot('striker', shot.angle, shot.power);
  }

  /**
   * Get the physics engine for running the simulation loop.
   */
  getPhysics(): CarromPhysics {
    return this.physics;
  }

  /**
   * Process the results after all bodies have stopped.
   * Returns the shot result and advances the turn.
   */
  processResults(shot: ShotInput): ShotResult {
    const player = this.state.currentTurn;
    const bodies = this.physics.getBodies();

    // Find pocketed coins this shot
    const pocketedCoins: CoinState[] = [];
    let strikerPocketed = false;
    let ownCoinPocketed = false;
    let opponentCoinPocketed = false;
    let queenPocketedThisShot = false;

    const playerAssignment = player === 'player1'
      ? this.state.player1Assignment
      : this.state.player2Assignment;

    for (const body of bodies) {
      if (body.pocketed) {
        if (body.type === 'striker') {
          strikerPocketed = true;
        } else {
          // Check if this coin was already pocketed before this shot
          const existingCoin = this.state.coins.find((c) => c.id === body.id);
          if (existingCoin && !existingCoin.pocketed) {
            const coinState: CoinState = {
              id: body.id,
              type: body.type,
              x: body.x,
              y: body.y,
              pocketed: true,
              pocketedBy: player,
            };
            pocketedCoins.push(coinState);

            if (body.type === playerAssignment) {
              ownCoinPocketed = true;
            } else if (body.type === 'queen') {
              queenPocketedThisShot = true;
            } else {
              opponentCoinPocketed = true;
            }
          }
        }
      }
    }

    // Determine foul
    let foul = false;
    let foulReason: string | undefined;

    if (strikerPocketed) {
      foul = true;
      foulReason = 'Striker pocketed';
    } else if (opponentCoinPocketed && !ownCoinPocketed && !queenPocketedThisShot) {
      foul = true;
      foulReason = "Pocketed opponent's coin without own coin";
    }

    // Handle queen cover logic
    let queenCovered = false;

    if (queenPocketedThisShot && !foul) {
      if (ownCoinPocketed) {
        // Queen covered in same shot
        queenCovered = true;
        this.state.queenPocketed = true;
        this.state.queenCovered = true;
        this.state.queenPocketedBy = player;
      } else {
        // Queen needs to be covered next shot
        this.state.pendingQueenCover = true;
        this.state.pendingQueenCoverPlayer = player;
        this.state.queenPocketed = true;
        this.state.queenPocketedBy = player;
      }
    } else if (this.state.pendingQueenCover && this.state.pendingQueenCoverPlayer === player) {
      if (ownCoinPocketed && !foul) {
        // Queen covered by pocketing own coin next turn
        queenCovered = true;
        this.state.queenCovered = true;
        this.state.pendingQueenCover = false;
      } else {
        // Failed to cover queen — return queen to center
        this.returnQueenToCenter();
        this.state.pendingQueenCover = false;
        this.state.pendingQueenCoverPlayer = undefined;
        this.state.queenPocketed = false;
        this.state.queenPocketedBy = undefined;
      }
    }

    // Handle foul penalties
    if (foul) {
      this.state.consecutiveFouls[player]++;
      this.handleFoulPenalty(player);

      // Return queen if pocketed during foul
      if (queenPocketedThisShot) {
        this.returnQueenToCenter();
        this.state.queenPocketed = false;
        this.state.queenPocketedBy = undefined;
        this.state.pendingQueenCover = false;
        this.state.pendingQueenCoverPlayer = undefined;
      }
    } else {
      this.state.consecutiveFouls[player] = 0;
    }

    // Update coin states
    for (const pocketed of pocketedCoins) {
      const coinIndex = this.state.coins.findIndex((c) => c.id === pocketed.id);
      if (coinIndex >= 0) {
        this.state.coins[coinIndex].pocketed = true;
        this.state.coins[coinIndex].pocketedBy = player;

        if (!foul) {
          if (player === 'player1') {
            this.state.player1PocketedCoins.push(pocketed);
          } else {
            this.state.player2PocketedCoins.push(pocketed);
          }
        }
      }
    }

    // Update scores
    this.updateScores();

    // Determine if turn continues
    const continueTurn = !foul && ownCoinPocketed;

    // Remove striker from physics
    this.physics.removeBody('striker');

    // Sync physics body positions back to coin states
    this.syncPhysicsToState();

    // Record move
    const result: ShotResult = {
      pocketedCoins,
      strikerPocketed,
      foul,
      foulReason,
      continueTurn,
      queenCovered,
    };

    this.state.moveHistory.push({
      moveNumber: this.state.moveHistory.length + 1,
      player,
      shot,
      result,
      timestamp: Date.now(),
    });

    this.state.lastMoveAt = Date.now();

    // Check win condition
    this.checkWinCondition();

    // Advance turn if needed
    if (!continueTurn && this.state.gameStatus.type === 'active') {
      this.advanceTurn();
    }

    return result;
  }

  // =========================================================================
  // Game Rules
  // =========================================================================

  private handleFoulPenalty(player: CarromPlayer): void {
    // Return one pocketed coin to the board center
    const pocketedList = player === 'player1'
      ? this.state.player1PocketedCoins
      : this.state.player2PocketedCoins;

    if (pocketedList.length > 0) {
      const returnedCoin = pocketedList.pop()!;
      const coinIndex = this.state.coins.findIndex((c) => c.id === returnedCoin.id);
      if (coinIndex >= 0) {
        this.state.coins[coinIndex].pocketed = false;
        this.state.coins[coinIndex].pocketedBy = undefined;

        // Place near center with slight offset to avoid overlap
        const offset = Math.random() * 20 - 10;
        this.state.coins[coinIndex].x = CENTER + offset;
        this.state.coins[coinIndex].y = CENTER + offset;

        // Update physics body
        this.physics.resetBody(
          returnedCoin.id,
          CENTER + offset,
          CENTER + offset
        );
      }
    }
  }

  private returnQueenToCenter(): void {
    const queenCoin = this.state.coins.find((c) => c.type === 'queen');
    if (queenCoin) {
      queenCoin.pocketed = false;
      queenCoin.pocketedBy = undefined;
      queenCoin.x = CENTER;
      queenCoin.y = CENTER;

      this.physics.resetBody('queen', CENTER, CENTER);
    }

    // Remove queen from pocketed lists
    this.state.player1PocketedCoins = this.state.player1PocketedCoins.filter(
      (c) => c.type !== 'queen'
    );
    this.state.player2PocketedCoins = this.state.player2PocketedCoins.filter(
      (c) => c.type !== 'queen'
    );
  }

  private updateScores(): void {
    this.state.player1Score = this.state.player1PocketedCoins.filter(
      (c) => c.type === this.state.player1Assignment
    ).length;
    this.state.player2Score = this.state.player2PocketedCoins.filter(
      (c) => c.type === this.state.player2Assignment
    ).length;

    // Queen bonus: +3 points if covered
    if (this.state.queenCovered && this.state.queenPocketedBy === 'player1') {
      this.state.player1Score += 3;
    } else if (this.state.queenCovered && this.state.queenPocketedBy === 'player2') {
      this.state.player2Score += 3;
    }
  }

  private checkWinCondition(): void {
    const p1Assignment = this.state.player1Assignment;
    const p2Assignment = this.state.player2Assignment;

    const p1RemainingCoins = this.state.coins.filter(
      (c) => c.type === p1Assignment && !c.pocketed
    ).length;
    const p2RemainingCoins = this.state.coins.filter(
      (c) => c.type === p2Assignment && !c.pocketed
    ).length;

    // Player 1 wins if all their assigned coins are pocketed
    // and queen is covered (if they pocketed it)
    if (p1RemainingCoins === 0) {
      const queenOk = !this.state.queenPocketed || this.state.queenCovered || this.state.queenPocketedBy !== 'player1';
      if (queenOk || this.state.pendingQueenCover === false) {
        this.state.gameStatus = { type: 'win', winner: 'player1' };
        return;
      }
    }

    if (p2RemainingCoins === 0) {
      const queenOk = !this.state.queenPocketed || this.state.queenCovered || this.state.queenPocketedBy !== 'player2';
      if (queenOk || this.state.pendingQueenCover === false) {
        this.state.gameStatus = { type: 'win', winner: 'player2' };
        return;
      }
    }

    // Check if all coins (including queen) are pocketed
    const allCoinsPocketed = this.state.coins.filter((c) => !c.pocketed).length === 0;
    if (allCoinsPocketed) {
      if (this.state.player1Score > this.state.player2Score) {
        this.state.gameStatus = { type: 'win', winner: 'player1' };
      } else if (this.state.player2Score > this.state.player1Score) {
        this.state.gameStatus = { type: 'win', winner: 'player2' };
      } else {
        this.state.gameStatus = { type: 'draw' };
      }
    }
  }

  advanceTurn(): void {
    this.state.currentTurn =
      this.state.currentTurn === 'player1' ? 'player2' : 'player1';
  }

  // =========================================================================
  // State Sync
  // =========================================================================

  private syncPhysicsToState(): void {
    for (const coin of this.state.coins) {
      const body = this.physics.getBody(coin.id);
      if (body && !coin.pocketed) {
        coin.x = body.x;
        coin.y = body.y;
      }
    }
  }

  // =========================================================================
  // Getters
  // =========================================================================

  getGameState(): CarromGameState {
    return { ...this.state };
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type !== 'active';
  }

  getCurrentPlayer(): CarromPlayer {
    return this.state.currentTurn;
  }

  getActiveCoins(): CoinState[] {
    return this.state.coins.filter((c) => !c.pocketed);
  }

  getPlayerAssignment(player: CarromPlayer): 'white' | 'black' {
    return player === 'player1'
      ? this.state.player1Assignment
      : this.state.player2Assignment;
  }
}
